
import { useEffect, useRef, useState } from "react";

import {
  ChevronDown,
  Maximize2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
} from "lucide-react";

import { useMusic } from "@/context/MusicContext";


export function BackgroundMusic({ enabled }) {

  // ---------------------------------------------------------
  // References and state
  // ---------------------------------------------------------

  const audioRef = useRef(null);

  const { currentSong, playSong } = useMusic();

  const [songs, setSongs] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [minimized, setMinimized] = useState(false);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia("(max-width: 767px)").matches;
  });

  const showMinimized = isMobile || minimized;


  // ---------------------------------------------------------
  // Detect mobile screen size
  // ---------------------------------------------------------

  useEffect(() => {

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };

  }, []);


  // ---------------------------------------------------------
  // Load the complete music library
  // ---------------------------------------------------------

  useEffect(() => {

    async function loadLibrary() {

      try {

        const response = await fetch("/data/song_library.json");

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const library = await response.json();

        const librarySongs = [...(library.songs || [])].sort(
          (a, b) => a.id - b.id
        );

        setSongs(librarySongs);

      } catch (error) {

        console.error(
          "Failed loading music library:",
          error
        );

      }

    }

    loadLibrary();

  }, []);


  // ---------------------------------------------------------
  // Load Song of the Day
  // ---------------------------------------------------------

  useEffect(() => {

    async function loadTodaySong() {

      try {

        console.log(
          "Loading Song of the Day from local library..."
        );

        const response = await fetch("/data/song_library.json");

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const library = await response.json();

        const librarySongs = [...(library.songs || [])].sort(
          (a, b) => a.id - b.id
        );

        if (!librarySongs.length) {
          throw new Error("Music library contains no songs.");
        }

        // Preserve existing daily song rotation.

        const today = new Date();

        const dateKey =
          today.getUTCFullYear() * 10000 +
          (today.getUTCMonth() + 1) * 100 +
          today.getUTCDate();

        const songIndex = dateKey % librarySongs.length;

        const song = librarySongs[songIndex];

        console.log("Song of the Day:", song);

        playSong({
          ...song,
          url: song.url || song.file,
        });

      } catch (error) {

        console.error(
          "Failed loading Song of the Day:",
          error
        );

      }

    }

    if (enabled && !currentSong) {
      loadTodaySong();
    }

  }, [enabled, currentSong, playSong]);


  // ---------------------------------------------------------
  // Load and play selected song
  // ---------------------------------------------------------

  useEffect(() => {

    const audio = audioRef.current;

    if (!audio || !currentSong) {
      return;
    }

    const songUrl = currentSong.url || currentSong.file;

    if (!songUrl) {

      console.error(
        "Current song has no audio URL:",
        currentSong
      );

      return;
    }

    audio.src = songUrl;
    audio.volume = 0.35;
    audio.load();

    setProgress(0);

    if (enabled) {

      audio
        .play()
        .then(() => {
          setPlaying(true);
        })
        .catch((error) => {

          console.error(
            "Playback failed:",
            error
          );

          setPlaying(false);

        });

    }

  }, [currentSong, enabled]);


  // ---------------------------------------------------------
  // Pause when audio consent is disabled
  // ---------------------------------------------------------

  useEffect(() => {

    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (!enabled) {

      audio.pause();
      setPlaying(false);

    }

  }, [enabled]);


  // ---------------------------------------------------------
  // Play / Pause
  // ---------------------------------------------------------

  const togglePlay = async () => {

    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (!audio.paused) {

      audio.pause();
      setPlaying(false);

      return;
    }

    try {

      await audio.play();
      setPlaying(true);

    } catch (error) {

      console.error(
        "Playback failed:",
        error
      );

      setPlaying(false);

    }

  };


  // ---------------------------------------------------------
  // Next song
  // ---------------------------------------------------------

  const playNext = () => {

    if (!songs.length || !currentSong) {
      return;
    }

    const currentIndex = songs.findIndex(
      (song) => song.id === currentSong.id
    );

    if (currentIndex === -1) {

      playSong(songs[0]);
      return;

    }

    const nextIndex =
      (currentIndex + 1) % songs.length;

    playSong(songs[nextIndex]);

  };


  // ---------------------------------------------------------
  // Previous song
  // ---------------------------------------------------------

  const playPrevious = () => {

    if (!songs.length || !currentSong) {
      return;
    }

    const currentIndex = songs.findIndex(
      (song) => song.id === currentSong.id
    );

    if (currentIndex === -1) {

      playSong(songs[0]);
      return;

    }

    const previousIndex =
      (currentIndex - 1 + songs.length) % songs.length;

    playSong(songs[previousIndex]);

  };


  // ---------------------------------------------------------
  // Song finished
  // ---------------------------------------------------------

  const handleEnded = () => {

    setPlaying(false);
    setProgress(0);

    playNext();

  };


  // ---------------------------------------------------------
  // Update playback progress
  // ---------------------------------------------------------

  const handleTimeUpdate = (event) => {

    const audio = event.target;

    if (
      !audio.duration ||
      !Number.isFinite(audio.duration)
    ) {

      setProgress(0);
      return;

    }

    setProgress(
      (audio.currentTime / audio.duration) * 100
    );

  };


  // ---------------------------------------------------------
  // Shared button styling
  // ---------------------------------------------------------

  const iconButtonClass = `
    flex
    h-8
    w-8
    shrink-0
    items-center
    justify-center
    border
    border-matrix-green/30
    text-matrix-green
    transition-all
    duration-200
    hover:border-matrix-green
    hover:bg-matrix-green/10
  `;


  // ---------------------------------------------------------
  // Audio element without selected song
  // ---------------------------------------------------------

  if (!currentSong) {

    return (
      <audio
        ref={audioRef}
        preload="auto"
      />
    );

  }


  // ---------------------------------------------------------
  // Main player
  // ---------------------------------------------------------

  return (

    <>

      {/* Audio stays mounted when switching layouts. */}

      <audio
        ref={audioRef}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
      />


      {/* Fixed player container */}

      <div
        className={`
          fixed
          bottom-5
          right-5
          z-[9999]
          max-w-[calc(100vw-2rem)]
          overflow-hidden
          border
          border-matrix-green/40
          bg-black/90
          shadow-[0_0_30px_rgba(0,255,100,0.08)]
          backdrop-blur-xl
          transition-[width]
          duration-300
          ${showMinimized ? "w-[300px]" : "w-[340px]"}
        `}
      >

        {showMinimized ? (

          // =================================================
          // MINIMIZED PLAYER
          // =================================================

          <div className="flex items-center gap-2 px-3 py-3">

            {/* Song title */}

            <div
              className="
                min-w-0
                flex-1
                truncate
                font-mono
                text-[11px]
                font-bold
                uppercase
                tracking-wide
                text-matrix-green
              "
              title={currentSong.title}
            >
              {currentSong.title}
            </div>


            {/* Previous */}

            <button
              type="button"
              onClick={playPrevious}
              className={iconButtonClass}
              aria-label="Previous song"
              title="Previous song"
            >
              <SkipBack size={15} />
            </button>


            {/* Play / Pause */}

            <button
              type="button"
              onClick={togglePlay}
              className={iconButtonClass}
              aria-label={playing ? "Pause" : "Play"}
              title={playing ? "Pause" : "Play"}
            >

              {playing ? (
                <Pause size={15} />
              ) : (
                <Play size={15} />
              )}

            </button>


            {/* Next */}

            <button
              type="button"
              onClick={playNext}
              className={iconButtonClass}
              aria-label="Next song"
              title="Next song"
            >
              <SkipForward size={15} />
            </button>


            {/* Expand - desktop only */}

            {!isMobile && (

              <button
                type="button"
                onClick={() => setMinimized(false)}
                className={iconButtonClass}
                aria-label="Expand player"
                title="Expand player"
              >
                <Maximize2 size={15} />
              </button>

            )}

          </div>

        ) : (

          // =================================================
          // EXPANDED PLAYER - DESKTOP ONLY
          // =================================================

          <>

            {/* Top terminal bar */}

            <div
              className="
                flex
                items-center
                justify-between
                border-b
                border-matrix-green/20
                bg-matrix-green/[0.04]
                px-4
                py-2
              "
            >

              {/* Live indicator */}

              <div
                className="
                  flex
                  items-center
                  gap-2
                  font-mono
                  text-[10px]
                  uppercase
                  tracking-[0.2em]
                  text-matrix-green
                "
              >

                <span
                  className="
                    h-1.5
                    w-1.5
                    animate-pulse
                    rounded-full
                    bg-matrix-green
                    shadow-[0_0_8px_rgba(0,255,100,0.8)]
                  "
                />

                AUDIO // LIVE

              </div>


              {/* Minimize */}

              <button
                type="button"
                onClick={() => setMinimized(true)}
                className="
                  flex
                  items-center
                  gap-1.5
                  border
                  border-matrix-green/40
                  px-2.5
                  py-1
                  font-mono
                  text-[10px]
                  uppercase
                  tracking-wider
                  text-matrix-green
                  transition-all
                  duration-200
                  hover:border-matrix-green
                  hover:bg-matrix-green/10
                "
                aria-label="Minimize player"
                title="Minimize player"
              >

                <ChevronDown size={14} />

                Minimize

              </button>

            </div>


            {/* Main player content */}

            <div className="p-5">

              {/* Song metadata */}

              <div className="flex items-start justify-between gap-4">

                <div className="min-w-0">

                  <div
                    className="
                      font-mono
                      text-[10px]
                      uppercase
                      tracking-[0.25em]
                      text-matrix-green/70
                    "
                  >
                    Now Playing
                  </div>


                  {/* Song title */}

                  <div
                    className="
                      mt-1
                      truncate
                      font-display
                      text-xl
                      font-bold
                      uppercase
                      tracking-wide
                      text-white
                    "
                  >
                    {currentSong.title}
                  </div>


                  {/* Genre and year */}

                  <div
                    className="
                      mt-1
                      font-mono
                      text-[10px]
                      uppercase
                      tracking-wider
                      text-white/40
                    "
                  >
                    {currentSong.genre} // {currentSong.year}
                  </div>

                </div>


                {/* Song ID */}

                <div
                  className="
                    shrink-0
                    border
                    border-white/10
                    px-2
                    py-1
                    font-mono
                    text-[9px]
                    text-white/30
                  "
                >
                  #{String(currentSong.id).padStart(2, "0")}
                </div>

              </div>


              {/* Progress bar */}

              <div className="mt-5">

                <div
                  className="
                    relative
                    h-[3px]
                    w-full
                    overflow-hidden
                    bg-white/10
                  "
                >

                  <div
                    className="
                      absolute
                      left-0
                      top-0
                      h-full
                      bg-matrix-green
                      shadow-[0_0_8px_rgba(0,255,100,0.6)]
                      transition-[width]
                      duration-150
                    "
                    style={{
                      width: `${progress}%`,
                    }}
                  />

                </div>

              </div>


              {/* Playback controls */}

              <div className="mt-5 flex items-center gap-3">

                {/* Previous */}

                <button
                  type="button"
                  onClick={playPrevious}
                  className={iconButtonClass}
                  aria-label="Previous song"
                  title="Previous song"
                >
                  <SkipBack size={16} />
                </button>


                {/* Play / Pause */}

                <button
                  type="button"
                  onClick={togglePlay}
                  className="
                    flex-1
                    border
                    border-matrix-green/60
                    bg-matrix-green/[0.04]
                    px-5
                    py-2.5
                    font-mono
                    text-[10px]
                    uppercase
                    tracking-[0.2em]
                    text-matrix-green
                    transition-all
                    duration-200
                    hover:border-matrix-green
                    hover:bg-matrix-green/10
                    hover:shadow-[0_0_15px_rgba(0,255,100,0.12)]
                    active:scale-[0.98]
                  "
                >

                  {playing ? "■ Pause" : "▶ Play"}

                </button>


                {/* Next */}

                <button
                  type="button"
                  onClick={playNext}
                  className={iconButtonClass}
                  aria-label="Next song"
                  title="Next song"
                >
                  <SkipForward size={16} />
                </button>

              </div>


              {/* Footer status */}

              <div
                className="
                  mt-4
                  flex
                  items-center
                  justify-between
                  border-t
                  border-white/5
                  pt-3
                  font-mono
                  text-[8px]
                  uppercase
                  tracking-[0.18em]
                  text-white/20
                "
              >

                <span>
                  DORIANTE // RADIO
                </span>

                <span>
                  {playing ? "STREAMING" : "STANDBY"}
                </span>

              </div>

            </div>

          </>

        )}

      </div>

    </>

  );

}
